import { useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Upload, X, Loader2, CheckCircle, AlertCircle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SAMPLE_CSV = `student_id,driveline_tag
100001,412
100002,213
100003,105
`;

export default function BulkUpdateDrivelineTags({ onClose, onSuccess }) {
  const [rawCsv, setRawCsv] = useState("");
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const handleSubmit = async () => {
    setError(null);
    setResult(null);
    const trimmed = rawCsv.trim();
    if (!trimmed) {
      setError("Paste the CSV data first.");
      return;
    }

    // Validate strict headers up-front for a clean UX
    const firstLine = trimmed.split(/\r?\n/)[0] || "";
    const headers = firstLine.split(",").map((h) => h.trim().toLowerCase());
    if (
      headers.length !== 2 ||
      !headers.includes("student_id") ||
      !headers.includes("driveline_tag")
    ) {
      setError(
        'The first line must be exactly "student_id,driveline_tag" — no other columns.',
      );
      return;
    }

    setUploading(true);
    try {
      const blob = new Blob([trimmed], { type: "text/csv" });
      const form = new FormData();
      form.append("file", blob, "driveline_tags.csv");

      const res = await axios.post(
        `${BACKEND_URL}/api/students/bulk-update-driveline-tags`,
        form,
        { withCredentials: true },
      );
      setResult(res.data);
      onSuccess?.();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-2xl bg-white max-h-[90vh] overflow-y-auto">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Upload className="w-5 h-5 text-yellow-600" />
            Bulk Update Driveline Tags
          </CardTitle>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            data-testid="close-driveline-modal"
          >
            <X className="w-5 h-5" />
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          <Alert className="bg-blue-50 border-blue-200">
            <AlertDescription className="text-sm text-blue-900">
              Paste CSV with exactly two columns:{" "}
              <code className="font-mono">student_id,driveline_tag</code>. Only the{" "}
              <code>driveline_tag</code> is updated on matching students — no other student
              fields are touched, and no new students are created. Rows with an unknown{" "}
              <code>student_id</code> or an empty tag are skipped.
            </AlertDescription>
          </Alert>

          <div>
            <label className="text-sm font-medium text-slate-700 mb-1 block">
              CSV data
            </label>
            <Textarea
              value={rawCsv}
              onChange={(e) => setRawCsv(e.target.value)}
              placeholder={SAMPLE_CSV}
              rows={10}
              className="font-mono text-sm"
              data-testid="driveline-csv-input"
            />
            <div className="mt-1 flex items-center gap-3">
              <button
                type="button"
                onClick={() => setRawCsv(SAMPLE_CSV)}
                className="text-xs text-blue-600 hover:underline"
              >
                Load sample
              </button>
              <span className="text-xs text-slate-400">
                First line must be:{" "}
                <code className="font-mono">student_id,driveline_tag</code>
              </span>
            </div>
          </div>

          {error && (
            <Alert className="bg-red-50 border-red-200">
              <AlertCircle className="w-4 h-4 text-red-600" />
              <AlertDescription className="text-red-800">{error}</AlertDescription>
            </Alert>
          )}

          {result && (
            <Alert className="bg-green-50 border-green-200">
              <CheckCircle className="w-4 h-4 text-green-600" />
              <AlertDescription className="text-green-900 space-y-1">
                <div className="font-medium">
                  Updated {result.updated} student tag{result.updated === 1 ? "" : "s"}.
                </div>
                <div className="text-xs">
                  Skipped: {result.skipped_no_match} unmatched student ID
                  {result.skipped_no_match === 1 ? "" : "s"} ·{" "}
                  {result.skipped_empty_tag} empty tag
                  {result.skipped_empty_tag === 1 ? "" : "s"}
                </div>
                {result.unmatched_ids?.length > 0 && (
                  <details className="text-xs mt-1">
                    <summary className="cursor-pointer text-slate-700">
                      Show unmatched IDs ({result.unmatched_ids.length}
                      {result.skipped_no_match > result.unmatched_ids.length
                        ? ` of ${result.skipped_no_match}`
                        : ""}
                      )
                    </summary>
                    <div className="mt-1 font-mono text-slate-600 max-h-32 overflow-y-auto">
                      {result.unmatched_ids.join(", ")}
                    </div>
                  </details>
                )}
              </AlertDescription>
            </Alert>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose} disabled={uploading}>
              {result ? "Close" : "Cancel"}
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={uploading || !rawCsv.trim()}
              className="bg-yellow-600 hover:bg-yellow-700"
              data-testid="driveline-upload-submit"
            >
              {uploading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Updating...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Update Tags
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
